import { getShuttleStopOptions } from "../../Lib/Utils/shuttleStops";
import { PtModeEnum } from "../../Orchestrator/Types/Enums/globalDefaultEnum";
import { IBooking, ILegTrain, ISelectedTrainLegs } from "../../Orchestrator/Types/Index";
import { sortE2ELegsByStartTime } from "../../Lib/Utils/buildE2ELegs";
import { RideHailingMode } from "../../Orchestrator/Types/Interface/IRideHailing";
import { IShuttleAddedJourney, IShuttleAddTarget } from "../../Orchestrator/Types/Interface/IShuttleBus";
import { getAllOfferWithoutExchange, getAllTripWithoutExchange } from "../../Lib/Utils/booking";
import { bookedShuttlesForLeg } from "../OnwardTravel/bookedShuttlesForLeg";
import { hasPendingExchange, OWNED_ADMISSION_STATUSES } from "../OnwardTravel/hasPendingExchange";

/**
 * The order ManageEndToEndRow renders a leg's two sides in. Used only to keep
 * the enumerated targets in the order the passenger sees them.
 */
const SHUTTLE_SIDE_ORDER: RideHailingMode[] = ["toStation", "fromStation"];

export interface IEligibleTargetsInput {
    booking: IBooking | undefined;
    /** Train legs in the order they are rendered — outbound before inbound. */
    legs: ILegTrain[];
    /** stopsResponseAtom: shuttle sub-stations hang off these stations. */
    allStops: any[];
    /** shuttleAddedAtom — an add made this session whose booking refresh lagged. */
    shuttleAdded: Partial<Record<RideHailingMode, IShuttleAddedJourney>>;
    /** refundedShuttleOfferIdsAtom — refunded here, not yet terminal on the booking. */
    refundedOfferIds: string[];
    isShuttleEnabled: boolean;
    isActiveBooking: boolean;
}

/**
 * The one train leg per direction that a ManageEndToEndRow actually renders —
 * sorted, then the first train leg, exactly as the row picks it. Everything that
 * reasons about shuttle eligibility (the Manage-booking menu gate, the request
 * coordinator) must count the same legs the row will offer, or the three drift.
 */
export function primaryTrainLegs(tripDetails: ISelectedTrainLegs | undefined): ILegTrain[] {
    return [tripDetails?.outbound, tripDetails?.inbound]
        .map((directionLegs) => sortE2ELegsByStartTime(directionLegs || []).find((leg) => leg?.trainType === "train"))
        .filter((leg): leg is ILegTrain => !!leg);
}

const stationRefForSide = (leg: ILegTrain, side: RideHailingMode): string =>
    (side === "toStation" ? leg.origin?.stopPlaceRef : leg.destination?.stopPlaceRef) || "";

/**
 * Every place this booking could still take a shuttle, in the order the
 * passenger sees them.
 *
 * Two consumers, one rule: the Manage-booking menu shows its item when this is
 * non-empty, and a ManageEndToEndRow expands the sides it names. Mirrors the
 * row's own eligibility checks — same stop lookup, same booking-derived
 * ownership, same pending-exchange block — so the menu never offers what the
 * section then refuses to show.
 */
export function eligibleShuttleTargets(input: IEligibleTargetsInput): IShuttleAddTarget[] {
    const { booking, legs, allStops, shuttleAdded, refundedOfferIds, isShuttleEnabled, isActiveBooking } = input;
    if (!isShuttleEnabled || !isActiveBooking || !booking) return [];
    // Turnit allows one uncommitted modification per booking: adding an offer
    // while an exchange awaits payment discards that exchange server-side.
    if (hasPendingExchange(booking)) return [];

    const ownedOfferIds = new Set(
        (booking.bookedOffers || [])
            .filter((offer) =>
                ((offer as { admissions?: { status?: string }[] })?.admissions || []).some((admission) =>
                    OWNED_ADMISSION_STATUSES.has(admission?.status || "")
                )
            )
            .map((offer) => (offer as { offerId?: string })?.offerId)
    );

    // Legs the booking does not own are a previous booking's, still in the
    // global atom — count nothing rather than counting the wrong thing.
    const ownLegs = (legs || []).filter((leg) => bookingOwnsLeg(booking, leg?.legId));
    if (ownLegs.length === 0) return [];

    const targets: IShuttleAddTarget[] = [];
    ownLegs.forEach((leg) => {
        if (!leg?.legId) return;
        // ownedOnly: the ownership check below happens AFTER the side has been
        // collapsed to one offer, so an unpaid offer reaching that point wins
        // the side and is then discarded — reopening a side that already holds
        // a paid shuttle. Exclusion has to happen before the collapse.
        const bookedSides = bookedShuttlesForLeg(booking, leg, refundedOfferIds, {
            ownedOnly: true,
        });
        SHUTTLE_SIDE_ORDER.forEach((side) => {
            const ref = stationRefForSide(leg, side);
            const stopOptions = getShuttleStopOptions(allStops || [], ref);
            if (stopOptions.length === 0) return;
            // Same two filters the row applies: the entry must be one this
            // booking owns AND belong to this side, or it is another
            // direction's shuttle leaking through the side-keyed atom.
            const candidate = bookedSides[side] ?? shuttleAdded?.[side];
            const stopIds = new Set(stopOptions.map((option) => option?.stopId));
            const alreadyAdded =
                !!candidate && ownedOfferIds.has(candidate.offerId) && stopIds.has(candidate.stopId);
            if (alreadyAdded) return;
            targets.push({ legId: leg.legId, side });
        });
    });
    return targets;
}

/**
 * The booking's TRAIN legs, in the shape the eligibility rule reads, straight
 * from a raw booking response.
 *
 * The bookings LIST has no `selectedTrainLegsAtom` for a row — that atom holds
 * whichever booking was opened last — so the list derives its legs here instead.
 * Shuttle legs are excluded: they are the product being added, not a place to
 * add one to.
 */
export function trainLegsFromBooking(booking: IBooking | undefined): ILegTrain[] {
    if (!booking) return [];
    // Exchanged-away trips still carry their legs on the booking, and
    // hasPendingExchange only screens exchanges still in flight — counting them
    // would offer a shuttle on a trip the passenger no longer holds.
    const liveTrips = getAllTripWithoutExchange(
        getAllOfferWithoutExchange(booking.bookedOffers || []),
        (booking.trips || []) as any
    );
    // ONE train leg per trip, mirroring primaryTrainLegs on the details side: a
    // non-direct trip has several, and counting each would make the list offer
    // more places than the section then shows.
    return (liveTrips || [])
        .map((trip: any) =>
            (trip?.legs || []).find((leg: any) => leg?.timedLeg?.service?.mode?.ptMode === PtModeEnum.TRAIN)
        )
        .filter(Boolean)
        .map((leg: any) => ({
            legId: leg?.id,
            origin: { stopPlaceRef: leg?.timedLeg?.start?.stopPlaceRef?.stopPlaceRef || "" },
            destination: { stopPlaceRef: leg?.timedLeg?.end?.stopPlaceRef?.stopPlaceRef || "" },
        }) as unknown as ILegTrain);
}

/**
 * Does this booking actually contain this leg?
 *
 * selectedTrainLegsAtom is a global that no page resets, and the booking-details
 * page writes bookingResponseAtom BEFORE the summary rebuilds the legs — so
 * there is a window where the booking is B and the legs are still A's. Comparing
 * ids is the only thing that catches it: A's legs are simply absent from B's
 * trips. Without this the eligibility count is computed against another
 * booking's legs and, because the request resolves once, latched forever.
 */
export function bookingOwnsLeg(booking: IBooking | undefined, legId: string | undefined): boolean {
    if (!booking || !legId) return false;
    return (booking.trips || []).some((trip: any) =>
        (trip?.legs || []).some((leg: any) => leg?.id === legId)
    );
}
